import React, { memo } from "react";
import { CheckCircleIcon, EmailIcon, NotAllowedIcon } from "@chakra-ui/icons";
import { Box } from "@chakra-ui/layout";
import {
  HStack,
  IconButton,
  SkeletonText,
  Link as ChakraLink,
} from "@chakra-ui/react";
import { Table, Tbody, Td, Th, Tr, Thead } from "@chakra-ui/table";
import { Tag } from "@chakra-ui/tag";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { Link } from "react-router-dom";
import { CopyText } from "../Utilities/CopyText";

interface UsersTableProps {
  users: CherryHubAdminApi.UserAccountSummaryItem[] | null;
  isLoading: boolean;
  debouncedQuery: string;
  disableActions?: boolean;
  onEnableUser: (user: CherryHubAdminApi.UserAccountSummaryItem) => void;
  onDisableUser: (user: CherryHubAdminApi.UserAccountSummaryItem) => void;
  onResetPasswordUser: (user: CherryHubAdminApi.UserAccountSummaryItem) => void;
}

export const UsersTable = memo(
  ({
    users,
    isLoading,
    disableActions,
    debouncedQuery,
    onEnableUser,
    onDisableUser,
    onResetPasswordUser,
  }: UsersTableProps) => {
    return (
      <Box w="100%" overflowX="auto">
        <Table size="sm">
          <Thead>
            <Tr>
              <Th whiteSpace="nowrap">User ID</Th>
              <Th>Identifer</Th>
              <Th>Name</Th>
              <Th>Status</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {!isLoading &&
              (users ?? []).map((user) => (
                <Tr key={user.Uid}>
                  <Td width="20%" whiteSpace="nowrap" title="Edit user.">
                    <CopyText text={user.Uid!} copyOnIcon={true} hover={true}>
                      <ChakraLink as={Link} to={`/admin/users/${user.Uid!}`}>
                        {user.Uid}
                      </ChakraLink>
                    </CopyText>
                  </Td>
                  <Td title="Edit user.">
                    <CopyText
                      text={user.Identifier!}
                      copyOnIcon={true}
                      hover={true}
                    >
                      <ChakraLink as={Link} to={`/admin/users/${user.Uid!}`}>
                        {user.Identifier}
                      </ChakraLink>
                    </CopyText>
                  </Td>
                  <Td title="Edit user.">
                    <ChakraLink as={Link} to={`/admin/users/${user.Uid!}`}>
                      {user.DisplayName}
                    </ChakraLink>
                  </Td>
                  <Td width="0.1%" whiteSpace="nowrap">
                    <Tag
                      size="sm"
                      bgColor={user.IsEnabled ? "" : "cherryButton.500"}
                      color={user.IsEnabled ? "" : "white"}
                    >
                      {user.IsEnabled ? "Enabled" : "Disabled"}
                    </Tag>
                  </Td>
                  <Td width="0.1%" whiteSpace="nowrap">
                    <HStack spacing={2} justifyContent="center">
                      {user.IsEnabled ? (
                        <IconButton
                          aria-label="Disable user."
                          title="Disable user."
                          icon={<NotAllowedIcon />}
                          size="xs"
                          isDisabled={disableActions}
                          onClick={() => onDisableUser(user)}
                        />
                      ) : (
                        <IconButton
                          aria-label="Enable user."
                          title="Enable user."
                          icon={<CheckCircleIcon />}
                          size="xs"
                          isDisabled={disableActions}
                          onClick={() => onEnableUser(user)}
                        />
                      )}

                      <IconButton
                        aria-label="Reset password."
                        title="Reset password."
                        icon={<EmailIcon />}
                        size="xs"
                        isDisabled={disableActions}
                        onClick={() => onResetPasswordUser(user)}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))}
            {users?.length === 0 && !isLoading && (
              <Tr>
                <Td colSpan={5} textAlign="center">
                  No user found matching query '{debouncedQuery}'
                </Td>
              </Tr>
            )}
            {isLoading && (
              <Tr>
                <Td width="20%">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="30%">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="40%">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="5%" whiteSpace="nowrap">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="5%" whiteSpace="nowrap">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
    );
  }
);
